/**
 * Wall-chain orientation and numbering.
 *
 * The chain is a closed loop where wall N's end is wall N+1's start. Two
 * separate concepts travel with it, and conflating them causes real bugs:
 *
 *   sequenceIndex  connectivity order. Covers walls AND openings.
 *   wallNumber     the label a designer sees. Walls only; openings get null.
 *
 * See ADR 0010 — a door is a GAP occupying real length in the chain, not an
 * abutment, so `segmentKind: 'opening'` entries are load-bearing geometry that
 * must never be numbered.
 *
 * Ported from reference/bluetooth-room-sketch-prototype/index.html, extended
 * for openings and clockwise-winding enforcement (ADR 0012).
 */
import { directionOf, toCvAngle } from './angles.js';

/** Segments that represent real wall surface (as opposed to an opening). */
const isWallSegment = (segment) => (segment.segmentKind ?? 'wall') === 'wall';

/** Tolerance for "the same height" / "the same x" when comparing candidates. */
const COINCIDENT = 0.01;

/**
 * Max perpendicular offset (canvas units) for two same-angle segments to count
 * as lying on the SAME line and therefore be the same wall. Since walls are
 * angle-snapped, two segments at the same angle are exactly parallel, so this
 * is just |their perpendicular separation|. Small, so genuinely parallel walls
 * a short distance apart are never fused.
 */
const COLLINEAR_TOLERANCE = 6;

/** True when two points are the same corner (within tolerance). */
function samePoint(a, b) {
  return Math.abs(a.x - b.x) <= COINCIDENT && Math.abs(a.y - b.y) <= COINCIDENT;
}

/**
 * Priority for a wall leaving the anchor corner, lower = better Wall 1.
 *
 * The direction is taken pointing AWAY from the anchor. A horizontal wall
 * heading east (a left return along the front) is the classic Wall 1, so it
 * ranks first; a vertical wall heading up (the left side wall, when there is no
 * return) ranks next. This encodes the owner's refinement — see the doc comment
 * on pickWallOneIndex.
 */
function awayRank(wall, anchor) {
  let angle = samePoint(wall.start, anchor) ? wall.internalAngle : (wall.internalAngle + 180) % 360;
  angle = ((angle % 360) + 360) % 360;

  if (angle <= 1 || angle >= 359) return 0; // east  — a horizontal return
  if (Math.abs(angle - 270) <= 1) return 1; // up    — the left side wall
  if (Math.abs(angle - 180) <= 1) return 2; // west
  if (Math.abs(angle - 90) <= 1) return 3; // down
  return 4 + angle / 360; // diagonals, ordered clockwise from east
}

/**
 * Index of the segment that should become Wall 1.
 *
 * Rule (`wall_one_selection_rule = bottom_most_horizontal`, refined
 * 2026-07-22): Wall 1 is the wall at the room's BOTTOM-LEFT CORNER, taken going
 * clockwise. In practice:
 *
 *   - With a left return beside the door, that horizontal return is Wall 1 —
 *     the common case, matching the owner's templates ("W1 begins at the left
 *     return").
 *   - With NO left return — the opening butts the bottom-left corner, so the
 *     first wall there is the VERTICAL left side wall — that vertical wall is
 *     Wall 1 (project owner, 2026-07-22). The previous rule wrongly picked the
 *     bottom-most horizontal wall, which in this case is the RIGHT return.
 *
 * Openings are excluded: an opening can never be Wall 1, and the corner it
 * occupies is still a wall endpoint (walls and openings share corners), so the
 * anchor is found correctly from wall endpoints alone.
 *
 * Canvas space has y growing DOWNWARD, so "bottom" is the LARGEST y.
 *
 * @returns {number} index into the ORIGINAL array (including openings)
 */
export function pickWallOneIndex(segments) {
  if (!segments.length) return 0;

  const candidates = segments
    .map((segment, index) => ({ segment, index }))
    .filter(({ segment }) => isWallSegment(segment));

  // Degenerate input (everything is an opening) — fall back to the whole set
  // rather than throwing, so a malformed sketch still renders.
  const pool = candidates.length ? candidates : segments.map((segment, index) => ({ segment, index }));

  // The bottom-left corner: among wall endpoints, the lowest (max y), ties
  // broken leftmost (min x).
  let anchor = null;
  for (const { segment } of pool) {
    for (const point of [segment.start, segment.end]) {
      if (
        anchor === null ||
        point.y > anchor.y + COINCIDENT ||
        (Math.abs(point.y - anchor.y) <= COINCIDENT && point.x < anchor.x - COINCIDENT)
      ) {
        anchor = point;
      }
    }
  }

  // Walls meeting that corner — one (no return) or two (with a return).
  const touching = pool.filter(({ segment }) => samePoint(segment.start, anchor) || samePoint(segment.end, anchor));
  if (touching.length === 0) return pool[0].index; // unreachable in practice; keeps the function total

  let best = touching[0];
  let bestRank = awayRank(best.segment, anchor);
  for (const candidate of touching.slice(1)) {
    const rank = awayRank(candidate.segment, anchor);
    if (rank < bestRank) {
      best = candidate;
      bestRank = rank;
    }
  }
  return best.index;
}

/** One segment reversed in place: endpoints swapped, both angles rotated 180. */
function flipSegment(segment) {
  const internalAngle = (segment.internalAngle + 180) % 360;
  return {
    ...segment,
    start: { ...segment.end },
    end: { ...segment.start },
    internalAngle,
    angleDeg: toCvAngle(internalAngle),
  };
}

/**
 * Reorders segments into geometric traversal order — wall N's end touching
 * wall N+1's start — flipping any segment drawn against the flow.
 *
 * Everything downstream (winding, Wall 1 rotation, collinear merging,
 * numbering) assumes the array IS the loop. That held automatically when a
 * room came from one stroke, but a designer who draws the room in several
 * strokes hands us DRAW order: the bottom edge added last, drawn
 * left-to-right, lands at the end of the array even though clockwise
 * traversal reaches its right piece first. The result was numbering that
 * jumped around the room (owner bug report, 2026-07-23: W5/W6 swapped on the
 * bottom wall). Ordering by connectivity first makes draw order, stroke
 * count, and stroke direction all irrelevant — as they must be.
 *
 * Greedy nearest-endpoint walk: from the current chain tail, take the unused
 * segment whose nearer endpoint is closest, flipped if its END is the closer
 * one. Nearest-not-exact matching matters twice over: separate strokes never
 * land corner-on-corner, and a designer may leave a genuine gap (a doorway)
 * that traversal must simply step across. For an already-ordered loop every
 * hop is exact, the walk reproduces the input, and the function is
 * idempotent.
 */
export function orderChainByConnectivity(segments) {
  if (segments.length <= 1) return segments.map((s) => ({ ...s }));

  const remaining = segments.map((s) => ({ ...s }));
  const chain = [remaining.shift()];

  while (remaining.length) {
    const tail = chain[chain.length - 1].end;
    let bestIndex = 0;
    let bestFlip = false;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const s = remaining[i];
      const dStart = Math.hypot(s.start.x - tail.x, s.start.y - tail.y);
      const dEnd = Math.hypot(s.end.x - tail.x, s.end.y - tail.y);
      if (dStart < bestDist) {
        bestDist = dStart;
        bestIndex = i;
        bestFlip = false;
      }
      if (dEnd < bestDist) {
        bestDist = dEnd;
        bestIndex = i;
        bestFlip = true;
      }
    }
    const [next] = remaining.splice(bestIndex, 1);
    chain.push(bestFlip ? flipSegment(next) : next);
  }
  return chain;
}

/**
 * Flips the traversal direction of a closed chain.
 *
 * Order is reversed and each segment's start/end swapped, so the loop still
 * walks start→end→start→end consistently — just the other way around. Both the
 * internal angle (0–359) and the stored Cabinet Vision angle rotate 180°.
 *
 * Recomputing `angleDeg` here is not optional: it is the value written to the
 * database and fed to Cabinet Vision, and it is now shown in the wall table. A
 * reversal that updated `internalAngle` but left `angleDeg` stale would display
 * — and export — the wrong direction for every reversed wall.
 */
export function reverseChain(segments) {
  return segments.slice().reverse().map(flipSegment);
}

/**
 * Signed area of the chain, using each segment's start point as a polygon
 * vertex (the loop is closed, so the last end ≈ the first start).
 *
 * Screen space has y growing DOWNWARD, which flips the usual sign convention:
 * a POSITIVE signed area here means the chain is wound CLOCKWISE as drawn on
 * screen. Verified against a known clockwise square.
 */
function signedArea(segments) {
  let sum = 0;
  for (let i = 0; i < segments.length; i++) {
    const a = segments[i].start;
    const b = segments[(i + 1) % segments.length].start;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

/**
 * Rotates and orients a chain so Wall 1 comes first and the numbering proceeds
 * in the configured direction (default clockwise).
 *
 * Two things happen, in order:
 *
 *   1. WINDING. The chain is reversed if needed so its traversal matches the
 *      `numbering_direction` business rule. This is what makes the numbers run
 *      consecutively clockwise around the room regardless of which way the
 *      designer happened to trace it — the actual requirement (owner, ADR 0012).
 *      An earlier version instead only forced Wall 1 to run left-to-right, which
 *      did NOT control the direction the rest of the numbers went.
 *
 *   2. ROTATION. Wall 1 is rotated to the front, so it is numbered 1 and its
 *      start becomes the anchor the closure solver walks from. Which wall that
 *      is comes from `options.wallOneId` when the designer has pinned one, and
 *      otherwise from pickWallOneIndex.
 *
 * The manual pin exists because the automatic rule is a heuristic about room
 * shape, and heuristics misfire: an L-shaped or irregular room can put the
 * geometric bottom-left corner somewhere the designer does not consider the
 * front of the room at all. Pinning overrides only the STARTING point — winding
 * is still enforced, so the rest of the walls continue in the configured
 * direction. That is the whole point: the designer names Wall 1, the standard
 * procedure numbers the rest.
 *
 * A pin that no longer resolves (the wall was merged away or deleted) falls
 * back to the automatic rule rather than throwing or leaving the chain
 * unrotated — a stale pin must never wedge the numbering.
 *
 * @param {object[]} segments
 * @param {object} [options]
 * @param {'clockwise'|'counterclockwise'} [options.numberingDirection]
 * @param {string|null} [options.wallOneId] segment id the designer pinned as Wall 1
 */
export function normalizeChain(segments, options = {}) {
  if (!segments.length) return [];
  const direction = options.numberingDirection ?? 'clockwise';

  // Connectivity FIRST: winding (signed area) and "adjacent" (collinear
  // merging) are meaningless unless the array already walks the perimeter.
  let ordered = orderChainByConnectivity(segments);

  // Positive signed area = clockwise on screen (y down). Reverse when the
  // current winding disagrees with the desired direction. A ~zero area (an open
  // or degenerate chain) leaves the order untouched.
  const area = signedArea(ordered);
  const wantsClockwise = direction !== 'counterclockwise';
  if ((wantsClockwise && area < 0) || (!wantsClockwise && area > 0)) {
    ordered = reverseChain(ordered);
  }

  // Pick Wall 1 AFTER winding (reversal changes start/end), then rotate it to
  // the front. reverseChain rebuilds objects, so re-find by id, not identity.
  // An opening can never be Wall 1, so a pin naming one is ignored.
  const pinned = options.wallOneId
    ? ordered.findIndex((s) => s.id === options.wallOneId && isWallSegment(s))
    : -1;

  let rotateFrom = pinned;
  if (rotateFrom < 0) {
    const wallOne = ordered[pickWallOneIndex(ordered)];
    const wallOneIndex = ordered.findIndex((s) => s.id === wallOne.id);
    rotateFrom = wallOneIndex >= 0 ? wallOneIndex : 0;
  }

  return ordered.slice(rotateFrom).concat(ordered.slice(0, rotateFrom));
}

/**
 * Merges consecutive collinear wall segments into one.
 *
 * Freehand strokes wobble, so a single straight wall a designer draws can snap
 * into two (or more) segments at the SAME angle — a spurious split, visible as
 * "why is my one wall numbered W3 and W4?". This walks the ordered chain and
 * fuses adjacent segments that are both walls, point the same direction, and
 * actually touch.
 *
 * Two segments merge when they are both walls, unmeasured, at the same angle,
 * and lie on the SAME line (small perpendicular offset). The along-line gap
 * between them does NOT have to be zero: freehand cleanup often leaves a few
 * units of gap between the pieces of one wall, and the closure solver re-pins
 * the chain from Wall 1's anchor afterwards regardless. Requiring exact
 * touching missed exactly the real-world split this exists to fix.
 *
 * It deliberately does NOT merge across an opening: a door produces
 * wall / opening / wall where the two walls are collinear but NOT adjacent (the
 * opening sits between them), so that intentional split (ADR 0010) is preserved.
 *
 * It also skips merging when either side is already measured, so a re-cleanup
 * cannot silently discard a length the designer typed. Fresh cleanup output is
 * all unmeasured, which is when spurious splits actually occur.
 *
 * Runs after ordering (so "adjacent" is meaningful) and before numbering (so the
 * numbers come out contiguous). Handles the wrap-around seam, where the last
 * segment is collinear with Wall 1, by extending Wall 1 backwards and dropping
 * the last — Wall 1 stays first.
 */
export function mergeCollinearWalls(segments) {
  if (segments.length < 2) return segments.map((s) => ({ ...s }));

  // Perpendicular distance from `b`'s start to the infinite line through `a`.
  // Zero means they share a line; large means they are parallel but offset.
  const offLine = (a, b) => {
    const dir = directionOf(a.internalAngle);
    const dx = b.start.x - a.start.x;
    const dy = b.start.y - a.start.y;
    return Math.abs(dx * dir.y - dy * dir.x);
  };

  const mergeable = (a, b) =>
    isWallSegment(a) &&
    isWallSegment(b) &&
    !a.isMeasured &&
    !b.isMeasured &&
    a.internalAngle === b.internalAngle &&
    offLine(a, b) <= COLLINEAR_TOLERANCE;

  const result = [];
  for (const segment of segments) {
    const prev = result[result.length - 1];
    if (prev && mergeable(prev, segment)) {
      result[result.length - 1] = { ...prev, end: { ...segment.end } };
    } else {
      result.push({ ...segment });
    }
  }

  // Wrap-around seam: the last wall may be the same wall as Wall 1, split at the
  // loop-closure point. This ONLY applies when the last segment actually meets
  // the first there (last.end ≈ first.start). Without that proximity check,
  // two collinear walls elsewhere in the loop — e.g. the two returns of a door,
  // which are collinear but held apart by the opening between them — would be
  // wrongly fused across the seam.
  if (result.length > 2) {
    const first = result[0];
    const last = result[result.length - 1];
    const meetsAtSeam = Math.hypot(last.end.x - first.start.x, last.end.y - first.start.y) <= COLLINEAR_TOLERANCE;
    if (meetsAtSeam && mergeable(last, first)) {
      result[0] = { ...first, start: { ...last.start } };
      result.pop();
    }
  }

  return result;
}

/**
 * Assigns `sequenceIndex` to every segment and `wallNumber` to walls only.
 *
 * Openings receive `wallNumber: null`, which is what the database's
 * `walls_number_matches_kind` CHECK requires. Numbering continues across an
 * opening without consuming a number — so a wall split around a door yields
 * W1, (opening), W2, exactly as the owner's templates show.
 *
 * @param {object[]} segments chain in traversal order, Wall 1 first
 * @param {object} [options]
 * @param {number} [options.startAt] first wall number (default 1)
 */
export function numberChain(segments, options = {}) {
  const startAt = options.startAt ?? 1;
  let nextNumber = startAt;

  return segments.map((segment, sequenceIndex) => ({
    ...segment,
    sequenceIndex,
    wallNumber: isWallSegment(segment) ? nextNumber++ : null,
  }));
}

/**
 * Full post-cleanup pass: orient the chain (winding + Wall 1 first), merge
 * spurious collinear splits, then number it. Order matters — orienting first so
 * "adjacent" is meaningful for the merge, and numbering last so the numbers come
 * out contiguous over the merged walls.
 *
 * @param {object[]} segments
 * @param {object} [options]
 * @param {'clockwise'|'counterclockwise'} [options.numberingDirection]
 * @param {string|null} [options.wallOneId] segment id the designer pinned as Wall 1
 * @param {number} [options.startAt]
 */
export function orientAndNumber(segments, options = {}) {
  return numberChain(mergeCollinearWalls(normalizeChain(segments, options)), options);
}
